interface OAuthAuthorizationRequest {
  response_type: 'code',
  client_id: string,
  redirect_uri: string,
  state: string,
  scope?: string,
  code_challenge: string,
  code_challenge_method: 'S256',
}

interface OAuthAuthorizationResult {
  code: string,
  state: string,
  redirect_uri: string,
}

type OAuthAuthorizationOutcome =
  'not_requested' | 'unauthenticated' | 'failed' | 'redirecting';

interface OAuthAuthorizationDependencies {
  getSearch: () => string,
  getAccessToken: () => string | undefined,
  clearAccessToken: () => void,
  authorize: (
    request: OAuthAuthorizationRequest,
  ) => Promise<{ status: number, data: any }>,
  replaceLocation: (url: string) => void,
}

const PKCE_CODE_CHALLENGE = /^[A-Za-z0-9._~-]{43,128}$/;

const isOAuthAuthorizationNavigation = (search: string) => {
  const params = new URLSearchParams(search);
  return params.get('response_type') === 'code' ||
    params.has('state') ||
    params.has('code_challenge');
};

const isHttpUrl = (value: string) => {
  try {
    const url = new URL(value);
    return (url.protocol === 'https:' || url.protocol === 'http:') &&
      !url.username &&
      !url.password;
  } catch (_error) {
    return false;
  }
};

const parseOAuthAuthorizationRequest = (
  search: string,
): OAuthAuthorizationRequest | null => {
  const params = new URLSearchParams(search);
  const responseType = params.get('response_type');
  const clientId = params.get('client_id') || '';
  const redirectUri = params.get('redirect_uri') || '';
  const state = params.get('state') || '';
  const scope = params.get('scope') || undefined;
  const codeChallenge = params.get('code_challenge') || '';
  const codeChallengeMethod = params.get('code_challenge_method');

  if (responseType !== 'code' ||
    !clientId ||
    clientId.length > 200 ||
    !redirectUri ||
    redirectUri.length > 2048 ||
    !isHttpUrl(redirectUri) ||
    !state ||
    state.length > 4096 ||
    !PKCE_CODE_CHALLENGE.test(codeChallenge) ||
    codeChallengeMethod !== 'S256' ||
    (scope && scope.length > 1000)) {
    return null;
  }

  return {
    response_type: 'code',
    client_id: clientId,
    redirect_uri: redirectUri,
    state,
    ...(scope ? { scope } : {}),
    code_challenge: codeChallenge,
    code_challenge_method: 'S256',
  };
};

const parseOAuthAuthorizationResult = (
  response: any,
  request: OAuthAuthorizationRequest,
): OAuthAuthorizationResult | null => {
  if (!response || response.code !== 0 || !response.result) return null;

  const result = response.result;
  if (typeof result.code !== 'string' ||
    !result.code ||
    result.state !== request.state ||
    result.redirect_uri !== request.redirect_uri) {
    return null;
  }

  return {
    code: result.code,
    state: result.state,
    redirect_uri: result.redirect_uri,
  };
};

const buildOAuthCallbackUrl = (result: OAuthAuthorizationResult) => {
  if (!isHttpUrl(result.redirect_uri)) return null;

  const callback = new URL(result.redirect_uri);
  callback.searchParams.set('code', result.code);
  callback.searchParams.set('state', result.state);
  return callback.toString();
};

const createOAuthSessionAuthorizer = (
  dependencies: OAuthAuthorizationDependencies,
) => {
  let inFlight: Promise<OAuthAuthorizationOutcome> | null = null;
  let loginPromptHandled = false;

  const performAuthorization = async (): Promise<OAuthAuthorizationOutcome> => {
    const search = dependencies.getSearch();
    const request = parseOAuthAuthorizationRequest(search);
    if (!request) return 'not_requested';
    if (!loginPromptHandled && new URLSearchParams(search).get('prompt') === 'login') {
      loginPromptHandled = true;
      dependencies.clearAccessToken();
      return 'unauthenticated';
    }
    if (!dependencies.getAccessToken()) return 'unauthenticated';

    try {
      const { status, data } = await dependencies.authorize(request);
      if (status === 401 || data?.code === -5 ||
        (typeof data?.code === 'number' && data.code.toString()[0] === '4')) {
        dependencies.clearAccessToken();
        return 'unauthenticated';
      }

      const result = parseOAuthAuthorizationResult(data, request);
      if (!result) return 'failed';

      const callbackUrl = buildOAuthCallbackUrl(result);
      if (!callbackUrl) return 'failed';

      dependencies.replaceLocation(callbackUrl);
      return 'redirecting';
    } catch (_error) {
      return 'failed';
    }
  };

  return (): Promise<OAuthAuthorizationOutcome> => {
    if (inFlight) return inFlight;

    const request = performAuthorization();
    inFlight = request;
    request.then(
      () => {
        if (inFlight === request) inFlight = null;
      },
      () => {
        if (inFlight === request) inFlight = null;
      },
    );
    return request;
  };
};

export type {
  OAuthAuthorizationDependencies,
  OAuthAuthorizationOutcome,
  OAuthAuthorizationRequest,
  OAuthAuthorizationResult,
};
export {
  buildOAuthCallbackUrl,
  createOAuthSessionAuthorizer,
  isOAuthAuthorizationNavigation,
  parseOAuthAuthorizationRequest,
  parseOAuthAuthorizationResult,
};
